<template>
    <flex-container :gap="Size.lg">
        <flex-container justify-content="space-between" align-items="center">
            <flex-container size="auto" :gap="Size.xs">
                <text-heading>
                    Documenten
                </text-heading>
            </flex-container>

            <flex-container size="auto" justify-content="flex-end">
                <button-collapse :options="createDocumentOptions" @click="createDocument">
                    <template #default>
                        Document genereren
                    </template>
                </button-collapse>
            </flex-container>
        </flex-container>

        <flex-container>
            <inline-tabs selected-name="user">
                <template #labels>
                    <inline-tab-label name="user" @click="selectedView = 'user'">
                        <chip-container>
                            Mijn documenten
                            <chip-informative small>
                                {{ userDocuments.length }}
                            </chip-informative>
                        </chip-container>
                    </inline-tab-label>

                    <inline-tab-label name="team" @click="selectedView = 'team'">
                        <chip-container>
                            Mijn team(s) documenten
                            <chip-informative small>
                                {{ teamDocuments.length }}
                            </chip-informative>
                        </chip-container>
                    </inline-tab-label>
                </template>
            </inline-tabs>

            <input-group v-model="selectedTypes" inline>
                <template #input>
                    <multi-select-input :items="selectedTypesItems">
                        <template #active>
                            <svg-icon :icon="IconFilter" />
                            <text-decorator>
                                Filter
                            </text-decorator>
                        </template>
                    </multi-select-input>
                </template>
            </input-group>

            <card-normal without-padding>
                <input-group v-model="searchFilter">
                    <template #input>
                        <text-input placeholder="Zoeken op (straat)naam" style="border-bottom-left-radius: 0; border-bottom-right-radius: 0">
                            <template #prefix>
                                <svg-icon :icon="IconSearch" :size="IconSize.sm" />
                            </template>
                        </text-input>
                    </template>
                </input-group>

                <list-container>
                    <template #header>
                        <list-cell-header>
                            <template #title>
                                Object of relatie
                            </template>
                        </list-cell-header>
                        <list-cell-header>
                            <template #title>
                                Aangemaakt door
                            </template>
                        </list-cell-header>
                        <list-cell-header>
                            <template #title>
                                Aanmaakdatum
                            </template>
                        </list-cell-header>
                        <list-cell-header>
                            <template #title>
                                Soort
                            </template>
                        </list-cell-header>
                        <list-cell-header>
                            <template #title>
                                Status
                            </template>
                        </list-cell-header>
                        <list-cell-header />
                    </template>

                    <template #content>
                        <list-row v-for="document in filteredDocuments" :key="document.id.value">
                            <list-cell-text>
                                <template #content>
                                    <template v-if="document instanceof Document">
                                        {{ document.lead.primaryContact.name }}
                                    </template>
                                    <template v-else>
                                        {{ document.wonenObjectAddress }}
                                    </template>
                                </template>
                            </list-cell-text>
                            <list-cell-text>
                                <template #content>
                                    <user-team-block
                                        :user="document.createdBy"
                                        :team="document.team"
                                    />
                                </template>
                            </list-cell-text>
                            <list-cell-text>
                                <template #content>
                                    {{ formatDate(document.createdAt) }}
                                </template>
                            </list-cell-text>
                            <list-cell-text>
                                <template #content>
                                    <template v-if="document instanceof Document">
                                        {{ document.template.layout.type.label }}
                                    </template>
                                    <template v-else>
                                        Woningbrochure
                                    </template>
                                </template>
                            </list-cell-text>
                            <list-cell-text>
                                <template #content>
                                    {{ document.rendererStatus.label }}
                                </template>
                            </list-cell-text>
                            <list-cell>
                                <flex-container
                                    align-content="center"
                                    justify-content="center"
                                    style="height: 100%"
                                >
                                    <button-outline-icon
                                        v-if="canDownloadDocument(document)"
                                        :icon="IconDownload"
                                        :color="Color.green500"
                                        @click="() => downloadDocument(document)"
                                    />
                                </flex-container>
                            </list-cell>
                        </list-row>
                    </template>
                </list-container>

                <flex-container
                    v-if="filteredDocuments.length === 0"
                    style="padding: 4rem;"
                    direction="column"
                    align-content="center"
                    align-items="center"
                >
                    <component :is="IllustrationDocument" style="max-width: 30vw" />
                    <text-heading>Geen documenten</text-heading>
                    <div v-if="documents.length === 0">
                        Er zijn nog geen documenten aangemaakt
                    </div>
                    <div v-else>
                        Er zijn geen documenten gevonden met de gekozen filters
                    </div>

                    <button-collapse
                        :options="createDocumentOptions"
                        variant="text"
                        horizontal-position="center"
                        @click="createDocument"
                    >
                        <template #default>
                            <svg-icon :icon="IconAdd" />
                            Aanmaken
                        </template>
                    </button-collapse>
                </flex-container>
            </card-normal>
        </flex-container>
    </flex-container>
</template>

<script setup lang="ts">
    import {
        ButtonCollapse,
        type ButtonCollapseOption,
        ChipContainer,
        ChipInformative,
        Color,
        FlexContainer,
        IconSize,
        InlineTabLabel,
        InlineTabs,
        MultiSelectInput,
        Size,
        TextHeading,
    } from '@keyvue/one-ui';

    import UserTeamBlock from '@components/Blocks/UserTeamBlock.vue';

    import {
        Brochure,
        Document,
        DocumentRenderStatus,
        DocumentType,
        DownloadBrochureCommand,
        DownloadDocumentCommand,
        RenderStatus as BrochureRenderStatus,
    } from '@keyvue/one-api-client';

    import { computed, ref } from 'vue';
    import { formatDate } from '@/utils/date';
    import { type RouteLocationRaw, useRouter } from 'vue-router';
    import useClient from '@composables/useClient';
    import useNotify from '@composables/useNotify';

    import IconAdd from '@keyvue/one-material-icons/icons/add.svg?component';
    import IconDownload from '@keyvue/one-material-icons/icons/download.svg?component';
    import IconFilter from '@keyvue/one-material-icons/icons/filter_alt.svg?component';
    import IconSearch from '@keyvue/one-material-icons/icons/search.svg?component';
    import IllustrationDocument from '@assets/illustrations/document.svg?component';

    const router = useRouter();
    const { client, getReactiveCollection, currentUser } = useClient();
    const { notifyError } = useNotify();

    const [
        brochureCollection,
        documentCollection,
    ] = await Promise.all([
        getReactiveCollection(Brochure, { 'order[updatedAt]': 'desc' }),
        getReactiveCollection(Document, { 'order[updatedAt]': 'desc' }),
    ]);

    const documents = computed(() => {
        const result = [
            ...brochureCollection.data.value,
            ...documentCollection.data.value,
        ];

        result.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -0));

        return result;
    });

    const userDocuments = computed(() => documents.value.filter((document) => document.createdBy.equals(currentUser.value)));
    const teamDocuments = computed(() => documents.value.filter((document) => !document.createdBy.equals(currentUser.value)));

    const selectedView = ref('user');

    const selectedTypesItems = computed(() => {
        const result = [
            {
                value: 'brochure',
                label: 'Woningbrochure',
            },
        ];

        DocumentType.values.forEach((documentType) => {
            result.push({
                value: documentType.value,
                label: documentType.label,
            });
        });

        return result;
    });
    const selectedTypes = ref([...selectedTypesItems.value]);

    const searchFilter = ref('');

    const filteredDocuments = computed(() => {
        let result: Array<Brochure | Document> = [];
        if (selectedView.value === 'user') {
            result = userDocuments.value;
        } else {
            result = teamDocuments.value;
        }

        const filteredTypes = selectedTypes.value.map((selectedType) => selectedType.value);

        result = result.filter((r) => {
            if (r instanceof Brochure) {
                return filteredTypes.includes('brochure');
            }

            return filteredTypes.includes(r.template.layout.type.value);
        });

        if (searchFilter.value) {
            const searchFilterValue = searchFilter.value.toLowerCase();

            result = result.filter((r) => {
                if (r instanceof Brochure) {
                    return r.wonenObjectAddress.toLowerCase().indexOf(searchFilterValue) !== -1;
                }
                return r.lead.primaryContact.name.fullName.toLowerCase().indexOf(searchFilterValue) !== -1
                    || r.lead.primaryContact.address.fullAddress.toLowerCase().indexOf(searchFilterValue) !== -1;
            });
        }

        return result;
    });

    const createDocumentOptions = computed(() => {
        const result: ButtonCollapseOption[] = [
            {
                value: 'brochure',
                label: 'Woningbrochure',
            },
        ];

        return result;
    });

    const createDocument = (documentType: string) => {
        let route: RouteLocationRaw = {
            name: 'documentGenerate',
            params: {
                type: documentType,
            },
        };

        if (documentType === 'brochure') {
            route = {
                name: 'documentGenerateBrochure',
            };
        }

        router.push(route);
    };

    const canDownloadDocument = (document: Brochure | Document) => {
        if (document instanceof Brochure) {
            return document.rendererStatus.equals(BrochureRenderStatus.SUCCESS);
        }

        return document.rendererStatus.equals(DocumentRenderStatus.SUCCESS);
    };

    const downloadDocument = async (document: Brochure | Document) => {
        let downloadDocumentCommand;
        if (document instanceof Brochure) {
            downloadDocumentCommand = new DownloadBrochureCommand(document.id.value);
        } else {
            downloadDocumentCommand = new DownloadDocumentCommand(document.id.value);
        }

        try {
            window.location = await client.dispatch(downloadDocumentCommand);
        } catch (error) {
            notifyError(new Error('Er is iets misgegaan met het downloaden van het document.'));
        }
    };
</script>

<style scoped lang="scss">
.list-row {
    button {
        opacity: 0;
    }

    &:hover {
        button {
            opacity: 1;
        }
    }
}
</style>
